import type { JSX, ValidComponent } from "@solidjs/web";
import { dynamic } from "@solidjs/web";
import { createMemo, omit } from "solid-js";
import { composeRefs, userRef } from "../../internals/compose-refs";
import { mergeRootProps } from "../../internals/merge-root-props";
import { resolveClassStyle } from "../../internals/resolve-class-style";
import type { BaseUIComponentProps } from "../../internals/types";
import type { TransitionStatus } from "../../internals/use-transition-status/use-transition-status";
import {
	type ComboboxAlign,
	type ComboboxSide,
	useComboboxPositionerContext,
} from "../positioner/positioner-context";
import { useComboboxRootContext } from "../root/combobox-root-context";

export interface ComboboxPopupState {
	open: boolean;
	side: ComboboxSide;
	align: ComboboxAlign;
	anchorHidden: boolean;
	transitionStatus: TransitionStatus;
	empty: boolean;
}

export interface ComboboxPopupProps
	extends BaseUIComponentProps<"div", ComboboxPopupState> {
	as?: ValidComponent;
}

/**
 * A container for the list. Renders a `<div>` element.
 */
export function ComboboxPopup(props: ComboboxPopupProps): JSX.Element {
	const root = useComboboxRootContext();
	const positioner = useComboboxPositionerContext();

	const setPopupRef = (el: HTMLElement | null): void => {
		root.popupRef.current = el;
	};

	const state = createMemo<ComboboxPopupState>(() => ({
		open: root.open(),
		side: positioner.side(),
		align: positioner.align(),
		anchorHidden: positioner.anchorHidden(),
		transitionStatus: root.transitionStatus(),
		empty: root.flatFilteredItems().length === 0,
	}));

	const internal = {
		role: "presentation" as const,
		tabIndex: -1,
		get "data-open"() {
			return state().open ? "" : undefined;
		},
		get "data-closed"() {
			return state().open ? undefined : "";
		},
		get "data-anchor-hidden"() {
			return state().anchorHidden ? "" : undefined;
		},
		get "data-side"() {
			return state().side;
		},
		get "data-align"() {
			return state().align;
		},
		get "data-starting-style"() {
			return state().transitionStatus === "starting" ? "" : undefined;
		},
		get "data-ending-style"() {
			return state().transitionStatus === "ending" ? "" : undefined;
		},
		get "data-empty"() {
			return state().empty ? "" : undefined;
		},
	};

	const userClassStyle = resolveClassStyle(props, () => state());
	const userProps = omit(props, "as", "class", "style", "attrs");

	let merged: Record<string, unknown> = mergeRootProps(
		internal,
		userClassStyle,
	);
	merged = mergeRootProps(merged, userProps);

	const Tag = dynamic(() => props.as ?? "div");
	return <Tag {...merged} ref={composeRefs(setPopupRef, userRef(props))} />;
}
